import React from 'react';
import { useIntl } from 'react-intl';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faRightFromBracket } from '@fortawesome/pro-light-svg-icons/faRightFromBracket';
import { TypedMemo } from '../common/types';
import { ConfirmDialog } from '../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../common/views/modals/confirmDialog/ConfirmDialogMark';

interface TeamConfirmModalProps {
  isOpen: boolean;
  title: string;
  /** The body: a member row, one paragraph, or both. */
  children: React.ReactNode;
  /** The danger button's label ("Remove", "Leave workspace"). */
  confirmLabel: string;
  /** The request runs: a spinner on the action, both buttons are disabled, and the modal cannot close. */
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** The title names another person (an email): keep it out of the Clarity session replay. */
  isTitleMasked?: boolean;
  /** The icon of the red mark. Defaults to "leave". */
  icon?: IconDefinition;
}

/**
 * Designs p1 and p2: one confirmation for Remove and Leave, with a danger button. It is the
 * shared confirm dialog (design dialogs a1) with the team copy.
 */
const TeamConfirmModalComponent: React.FC<TeamConfirmModalProps> = ({
  isOpen,
  title,
  children,
  confirmLabel,
  isPending,
  onConfirm,
  onCancel,
  isTitleMasked = false,
  icon = faRightFromBracket,
}) => {
  const intl = useIntl();

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={icon} />}
      title={title}
      isTitleMasked={isTitleMasked}
      cancelLabel={intl.formatMessage({ id: 'app.sml.team.confirm.cancel', defaultMessage: 'Cancel' })}
      confirmLabel={confirmLabel}
      isBusy={isPending}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {children}
    </ConfirmDialog>
  );
};

export const TeamConfirmModal = TypedMemo(TeamConfirmModalComponent);
